/**
 * CategoryListItem Component
 *
 * REUSABLE generic row component for displaying category items.
 * Works with any category type (Market Condition, Entry Model).
 *
 * Features:
 * - Color swatch display
 * - Name and description (truncated)
 * - Slot for "used in" content (specific to category type)
 * - Action menu (Edit, Archive/Restore)
 * - Archived state styling
 */

import React from 'react';
import { MoreVertical } from 'lucide-react';

import {
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from '@enfomentis/ui';
import type { EntityStatus } from '@/types/shared/EntityStatus';

export interface CategoryListItemProps {
  /** The category data to display */
  category: {
    id: string;
    name: string;
    description?: string;
    color: string;
    status: EntityStatus;
  };
  /** Slot for "used in" content - specific to category type */
  usedInContent: React.ReactNode;
  /** Handler for edit action */
  onEdit: () => void;
  /** Handler for archive click (opens confirmation in parent) */
  onArchiveClick: () => void;
  /** Handler for restore action */
  onRestore: () => void;
}

export const CategoryListItem: React.FC<CategoryListItemProps> = ({
  category,
  usedInContent,
  onEdit,
  onArchiveClick,
  onRestore,
}) => {
  const isArchived = category.status === 'archived';

  return (
    <div
      className={`flex items-center gap-3 p-3 ${
        isArchived ? 'opacity-60 bg-default-50' : 'bg-content1'
      }`}
    >
      {/* Color Swatch */}
      <div className="shrink-0">
        <span
          className="w-4 h-4 rounded-full block"
          style={{ backgroundColor: category.color }}
          aria-hidden="true"
        />
      </div>

      {/* Name + Description + Archived Badge */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center min-w-0">
          <span
            className={`font-medium truncate ${
              isArchived ? 'text-text-tertiary' : 'text-text-primary'
            }`}
          >
            {category.name}
          </span>
          {isArchived && (
            <Chip className="ml-2 shrink-0 text-xs text-text-tertiary bg-default-100">
              Archived
            </Chip>
          )}
        </div>
        {category.description && (
          <span className="text-sm text-text-secondary line-clamp-1 block">
            {category.description}
          </span>
        )}
      </div>

      {/* Used In Content Slot */}
      <div className="shrink-0">
        {usedInContent}
      </div>

      {/* Actions Menu */}
      <div className="shrink-0">
        <Dropdown>
          <DropdownTrigger>
            <Button
              size="sm"
              variant="light"
              isIconOnly
              aria-label="Category actions"
            >
              <MoreVertical size={16} />
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Category actions">
            <DropdownItem key="edit" onPress={onEdit}>
              Edit
            </DropdownItem>
            {isArchived ? (
              <DropdownItem key="restore" onPress={onRestore}>
                Restore
              </DropdownItem>
            ) : (
              <DropdownItem
                key="archive"
                onPress={onArchiveClick}
                className="text-warning-600"
              >
                Archive
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
};
